/* Shared helpers for every bundle builder page (anything with a [data-bundle-form]).
   1. Shopify's cart/add.js can add part of a multi-item request and still answer with an error when one item is short of stock.
      This makes the add all-or-nothing: on a partial add it puts the basket back as it was, lowers the short items on the page and tells the customer why.
   2. If a filter (scent, sign, mood, chakra) leaves a step with nothing to choose, the step shows all of its products again with a short note, so a required step can never be a dead end.
   3. A product that appears in more than one step is kept in the first step only, so it cannot be added twice by accident.
   4. The add button is always usable again when the customer comes back to the page (Back button, restored tab).
   Auro: 5. Also covers the gift box builder ([data-gift-builder]), so a stock shortfall there no longer leaves a half-built box.
   Auro: 6. After a bundle is added, pressing Back reloads the builder from fresh, so the same bundle cannot be added twice by accident.
   Auro: 7. Tags every line a step builder adds (packaging included) with hidden line properties _bundle_id (new for each add) and
            _bundle (the builder's name). The basket groups lines by _bundle_id, and the unique id stops a second identical bundle,
            or the same product bought singly, merging into these lines. Underscore keys stay hidden in the basket and at checkout. */
(() => {
  if (window.__bundleCartGuard) return;
  window.__bundleCartGuard = true;
  const nativeFetch = window.fetch.bind(window);
  const base = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  /* Auro: the gift box builder counts as a bundle page too. */
  const isBundlePage = () => !!document.querySelector('[data-bundle-form], [data-gift-builder]');
  /* Auro: the name shown in the basket as "<name> bundle", found from the builder section's own data attribute. */
  const BUNDLE_NAMES = [
    ['data-agnes-bundle', 'Agnes + Cat'],
    ['data-autumn-bundle', 'Autumn'],
    ['data-bath-ritual', 'Bath ritual'],
    ['data-chakra-bundle', 'Chakra and meditation'],
    ['data-christmas-bundle', 'Christmas gift'],
    ['data-home-fragrance', 'Home fragrance'],
    ['data-meaning-bundle', 'Meaning'],
    ['data-tarot-witch', 'Tarot and witchcraft'],
    ['data-zodiac-bundle', 'Zodiac']
  ];
  const newBundleId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  /* Auro: adds the hidden bundle properties, but only when the request is the builder's own submit: every item matches a
     picked card in the builder at the picked quantity, and nothing else is in the request. Quick adds elsewhere on the page are left alone. */
  const tagBuilderItems = body => {
    const form = document.querySelector('[data-bundle-form]');
    if (!form) return body;
    let data;
    try { data = JSON.parse(body); } catch (error) { return body; }
    const items = data && data.items;
    if (!Array.isArray(items) || !items.length) return body;
    if (items.some(i => i && i.properties && Object.keys(i.properties).length)) return body;
    let host = null;
    let name = '';
    BUNDLE_NAMES.forEach(([attr, label]) => { if (!host) { host = form.closest('[' + attr + ']'); if (host) name = label; } });
    if (!host) host = form;
    if (!name) { const t = host.querySelector('.ab__title'); name = t ? t.textContent.trim() : 'Gift'; }
    const picked = {};
    host.querySelectorAll('[data-card]').forEach(card => {
      const q = Number(card.dataset.qty || 0);
      if (q > 0 && card.dataset.variantId) picked[String(card.dataset.variantId)] = (picked[String(card.dataset.variantId)] || 0) + q;
    });
    if (Object.keys(picked).length !== items.length) return body;
    if (!items.every(i => picked[String(i.id)] === Number(i.quantity))) return body;
    const properties = { _bundle_id: newBundleId(), _bundle: name };
    data.items = items.map(i => Object.assign({}, i, { properties }));
    return JSON.stringify(data);
  };
  /* Auro: remembers which builder page just added a bundle (see the pageshow handler at the end). */
  const ADDED_KEY = 'auro_bundle_added';
  const markAdded = (response, body) => {
    try {
      if (!response.ok) return;
      const items = JSON.parse(body).items || [];
      if (items.some(i => i && i.properties && i.properties._bundle_id)) window.sessionStorage.setItem(ADDED_KEY, window.location.pathname);
    } catch (error) { /* storage blocked: nothing to remember */ }
  };
  const getCart = () => nativeFetch(base() + 'cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
  const say = text => {
    const set = () => { document.querySelectorAll('[data-bundle-hint]').forEach(el => { el.textContent = text; }); };
    set();
    window.setTimeout(set, 60);
    window.setTimeout(set, 400);
  };
  const cardFor = id => document.querySelector(`[data-card][data-variant-id='${id}']`);
  const titleFor = (id, line) => {
    if (line && line.product_title) return line.product_title;
    /* Auro: the gift box builder lists its item names here, because its cards are not all on screen. */
    const named = window.__auroBundleTitles && window.__auroBundleTitles[id];
    if (named) return named;
    const card = cardFor(id);
    const t = card && card.querySelector('[class*=card__title]');
    return t ? t.textContent.trim() : 'One item';
  };
  /* Brings a card down to the quantity that is really available, in one step, by using the card's own minus or add button. */
  const lowerCardTo = (card, target) => {
    if (Number(card.dataset.qty || 0) <= target) return;
    if (card.dataset.mode === 'single') { const add = card.querySelector('[data-add]'); if (add && target <= 0) add.click(); return; }
    const minus = card.querySelector('[data-minus]');
    if (!minus) return;
    card.dataset.qty = String(Math.max(0, target) + 1);
    minus.click();
  };

  const ctaButtons = () => document.querySelectorAll('[data-bundle-cta], [data-gb-cta]');
  const resetButtons = () => { ctaButtons().forEach(btn => { btn.disabled = false; if (btn.dataset.label) btn.textContent = btn.dataset.label; }); };

  /* Runs before each builder starts up (this file is deferred, the builders wait for DOMContentLoaded). */
  const removeDuplicates = () => {
    const form = document.querySelector('[data-bundle-form]');
    if (!form) return;
    const seen = {};
    const touched = [];
    form.querySelectorAll('[data-card]').forEach(card => {
      const id = card.dataset.variantId;
      if (!id) return;
      if (seen[id]) { const step = card.closest('[data-step]'); card.remove(); if (step && touched.indexOf(step) < 0) touched.push(step); } else seen[id] = true;
    });
    touched.forEach(step => {
      const more = step.querySelector('[data-more]');
      if (!more) return;
      const total = step.querySelectorAll('[data-card]').length;
      const fix = text => String(text || '').split(' ').map(word => (word !== '' && !isNaN(Number(word)) ? String(total) : word)).join(' ');
      if (more.dataset.label) more.dataset.label = fix(more.dataset.label);
      more.textContent = fix(more.textContent);
    });
  };
  removeDuplicates();

  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isAdd = url.indexOf('cart/add.js') > -1 && init && String(init.method || '').toUpperCase() === 'POST' && typeof init.body === 'string' && isBundlePage();
    if (!isAdd) return nativeFetch(input, init);
    init = Object.assign({}, init, { body: tagBuilderItems(init.body) });
    let wanted = [];
    try { wanted = (JSON.parse(init.body).items || []).map(i => ({ id: Number(i.id), quantity: Number(i.quantity) || 1 })); } catch (error) { wanted = []; }
    if (wanted.length < 2) { const single = await nativeFetch(input, init); markAdded(single, init.body); return single; }
    let before = null;
    try { before = await getCart(); } catch (error) { before = null; }
    const response = await nativeFetch(input, init);
    if (response.ok) { markAdded(response, init.body); window.setTimeout(resetButtons, 4000); return response; }
    if (!before) return response;
    let after = null;
    try { after = await getCart(); } catch (error) { return response; }
    const prev = {};
    before.items.forEach(line => { prev[line.key] = line.quantity; });
    const updates = {};
    const added = {};
    const lines = {};
    let changed = false;
    after.items.forEach(line => {
      const was = prev[line.key] || 0;
      if (line.quantity > was) {
        updates[line.key] = was;
        added[line.variant_id] = (added[line.variant_id] || 0) + (line.quantity - was);
        lines[line.variant_id] = line;
        changed = true;
      }
    });
    if (!changed) {
      try { const data = await response.clone().json(); if (data && (data.description || data.message)) say(data.description || data.message); } catch (error) { /* keep the page's own message */ }
      return response;
    }
    /* Auro: only claim that nothing was added when the roll back really worked. */
    let rolledBack = false;
    try {
      const undo = await nativeFetch(base() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ updates }) });
      rolledBack = undo.ok;
    } catch (error) { rolledBack = false; }
    const notes = [];
    const short = [];
    wanted.forEach(item => {
      const got = added[item.id] || 0;
      if (got >= item.quantity) return;
      const title = titleFor(item.id, lines[item.id]);
      const card = cardFor(item.id);
      if (card) lowerCardTo(card, got);
      notes.push(got > 0 ? 'only ' + got + ' of ' + title + ' in stock' : title + ' is out of stock');
      short.push({ id: item.id, available: got });
    });
    /* Auro: lets the gift box builder lower its own picks to what is in stock. */
    try { document.dispatchEvent(new CustomEvent('auro:bundle-short', { detail: { items: short } })); } catch (error) { /* older browser */ }
    if (!rolledBack) say('Some items could not be added' + (notes.length ? ' (' + notes.join('; ') + ')' : '') + '. Please check your basket before trying again.');
    else say(notes.length ? 'Nothing has been added yet: ' + notes.join('; ') + '. We have adjusted your choices, so check them and press the button again.' : 'Some items were not available, so nothing has been added. Please check your choices and try again.');
    return new Response(JSON.stringify({ status: 422, message: 'Bundle not added', description: 'Bundle not added' }), { status: 422, headers: { 'Content-Type': 'application/json' } });
  };

  const refillEmptySteps = () => {
    document.querySelectorAll('[data-bundle-form] [data-step]').forEach(step => {
      const cards = [...step.querySelectorAll('[data-card]')];
      if (!cards.length || cards.some(card => !card.hidden)) return;
      cards.forEach(card => { card.hidden = false; });
      const grid = step.querySelector('[data-grid]');
      if (!grid || step.querySelector('[data-empty-note]')) return;
      const note = document.createElement('p');
      note.setAttribute('data-empty-note', '');
      note.style.cssText = 'margin:0 0 1.6rem;font-size:1.4rem;line-height:1.5;color:rgba(61,61,61,.78)';
      note.textContent = 'Nothing in this step matches that choice, so here is everything instead.';
      grid.parentElement.insertBefore(note, grid);
    });
  };
  const isFilterButton = el => !!el && [...el.attributes].some(attr => attr.name.indexOf('filter') > -1);
  document.addEventListener('click', event => {
    const button = event.target && event.target.closest ? event.target.closest('button') : null;
    if (!isFilterButton(button) || !isBundlePage()) return;
    document.querySelectorAll('[data-empty-note]').forEach(note => note.remove());
    window.setTimeout(refillEmptySteps, 40);
  });

  const remember = () => {
    ctaButtons().forEach(btn => { if (!btn.dataset.label) btn.dataset.label = btn.textContent; });
    window.setTimeout(refillEmptySteps, 300);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', remember, { once: true }); else remember();
  /* Fires on every arrival at the page, including a return through the Back button from the basket. */
  window.addEventListener('pageshow', event => {
    /* Auro: back from the basket after adding a bundle. A page restored from the back/forward cache still holds the old picks
       and an active button, so reload it to start fresh instead of letting a second tap add the same bundle again. */
    try {
      if (window.sessionStorage.getItem(ADDED_KEY) === window.location.pathname) {
        window.sessionStorage.removeItem(ADDED_KEY);
        if (event.persisted) { window.location.reload(); return; }
      }
    } catch (error) { /* storage blocked */ }
    remember();
    resetButtons();
  });
})();